/**
 * Dot-Grid Canvas Background with Mouse + Cursor Reveal
 *
 * Reads ALL cursor positions from cursorStore (real mouse + animated cursors).
 * Base grid always visible at BASE_ALPHA. Active cursors brighten nearby dots.
 */
import { useEffect, useRef } from 'react';

import {
  cursorStore,
  deactivateCursor,
  moveCursorTo,
  registerCursor,
} from './cursor-store';

const SPACING = 32;
const DOT_R = 1.5;
const BASE_ALPHA = 0.12;
const MAX_ALPHA = 0.45;
const FADE_PER_SEC = 0.4;
const COLOR_LIGHT = '80,144,211';
const COLOR_DARK = '140,190,240';

export function DotGridCanvas({ dark = false }: { dark?: boolean }) {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const alphas = new Map<number, number>();
    let cols = 0;
    let rows = 0;
    let animId = 0;
    let lastTime = 0;
    let running = false;

    // Register real mouse as a cursor in the shared store
    const mouseCursor = registerCursor();

    const revealR = () => (window.innerWidth < 768 ? 280 : 600);

    function measure() {
      const dpr = Math.min(window.devicePixelRatio, 2);
      const rect = canvas!.parentElement!.getBoundingClientRect();
      canvas!.width = Math.round(rect.width) * dpr;
      canvas!.height = Math.round(rect.height) * dpr;
      ctx!.setTransform(dpr, 0, 0, dpr, 0, 0);
      cols = Math.ceil(rect.width / SPACING) + 1;
      rows = Math.ceil(rect.height / SPACING) + 1;
    }

    function reveal(cx: number, cy: number) {
      const r = revealR();
      const colMin = Math.max(0, Math.floor((cx - r) / SPACING));
      const colMax = Math.min(cols - 1, Math.ceil((cx + r) / SPACING));
      const rowMin = Math.max(0, Math.floor((cy - r) / SPACING));
      const rowMax = Math.ceil((cy + r) / SPACING);

      for (let row = rowMin; row <= rowMax; row++) {
        for (let col = colMin; col <= colMax; col++) {
          const dx = col * SPACING - cx;
          const dy = row * SPACING - cy;
          const dist = Math.hypot(dx, dy);
          if (dist < r) {
            const t = (1 - dist / r) ** 2;
            const a = t * MAX_ALPHA;
            const key = row * cols + col;
            if (a > (alphas.get(key) ?? 0)) alphas.set(key, a);
          }
        }
      }
    }

    function drawBase() {
      const w = canvas!.clientWidth;
      const h = canvas!.clientHeight;
      ctx!.clearRect(0, 0, w, h);
      const color = dark ? COLOR_DARK : COLOR_LIGHT;
      ctx!.fillStyle = `rgba(${color},${BASE_ALPHA})`;
      for (let row = 0; row <= rows; row++) {
        for (let col = 0; col <= cols; col++) {
          ctx!.beginPath();
          ctx!.arc(col * SPACING, row * SPACING, DOT_R, 0, Math.PI * 2);
          ctx!.fill();
        }
      }
    }

    function startLoop() {
      if (!running) {
        running = true;
        lastTime = 0;
        animId = requestAnimationFrame(loop);
      }
    }

    function loop(time: number) {
      if (!lastTime) lastTime = time;
      const dt = (time - lastTime) / 1000;
      lastTime = time;

      // Reveal around ALL active cursors (mouse + animated)
      const canvasRect = canvas!.getBoundingClientRect();
      let hasActiveCursor = false;
      for (const cursor of cursorStore) {
        if (!cursor.active) continue;
        hasActiveCursor = true;
        // Convert viewport coords to canvas-local coords
        const localX = cursor.x - canvasRect.left;
        const localY = cursor.y - canvasRect.top;
        reveal(localX, localY);
      }

      // Fade
      const fadeAmt = FADE_PER_SEC * dt;
      for (const [key, alpha] of alphas) {
        const next = alpha - fadeAmt;
        if (next < 0.005) {
          alphas.delete(key);
        } else {
          alphas.set(key, next);
        }
      }

      // Draw base + revealed overlay
      drawBase();
      const color = dark ? COLOR_DARK : COLOR_LIGHT;
      for (const [key, revealAlpha] of alphas) {
        if (revealAlpha <= BASE_ALPHA) continue;
        const row = Math.floor(key / cols);
        const col = key % cols;
        ctx!.fillStyle = `rgba(${color},${revealAlpha})`;
        ctx!.beginPath();
        ctx!.arc(col * SPACING, row * SPACING, DOT_R, 0, Math.PI * 2);
        ctx!.fill();
      }

      if (alphas.size > 0 || hasActiveCursor) {
        animId = requestAnimationFrame(loop);
      } else {
        running = false;
        drawBase();
      }
    }

    function onMouseMove(e: MouseEvent) {
      moveCursorTo(mouseCursor, e.clientX, e.clientY);
      startLoop();
    }

    function onMouseLeave() {
      deactivateCursor(mouseCursor);
    }

    measure();
    drawBase();
    startLoop(); // Start loop — animated cursors may already be active

    window.addEventListener('resize', () => {
      measure();
      alphas.clear();
      drawBase();
    });

    const parent = canvas.parentElement!;
    parent.addEventListener('mousemove', onMouseMove, { passive: true });
    parent.addEventListener('mouseleave', onMouseLeave);

    return () => {
      running = false;
      cancelAnimationFrame(animId);
      deactivateCursor(mouseCursor);
      parent.removeEventListener('mousemove', onMouseMove);
      parent.removeEventListener('mouseleave', onMouseLeave);
    };
  }, [dark]);

  return (
    <canvas
      ref={canvasRef}
      style={{
        position: 'absolute',
        inset: 0,
        width: '100%',
        height: '100%',
        pointerEvents: 'none',
        zIndex: 0,
      }}
    />
  );
}
